import React, { useEffect, useState, useCallback, useRef } from "react";
import Box from "@mui/material/Box";
import Layout from "../../common/Layout";
import ChatSidebar from "./ChatSideBar";
import ChatWindow from "./ChatWindow";
import InputDialog from "./InputDialog.js";
import { useNavigate, useParams } from "react-router-dom";
import config from "../../../config/Config.js";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";

const Chatbot = () => {
  const navigate = useNavigate();
  const { sessionId: activeSessionId = null } = useParams();
  const [snackbar, setSnackbar] = useState({ open: false, message: "" });
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [chatKey, setChatKey] = useState(0);
  const [historyList, setHistoryList] = useState([]);
  const [groupedChatsList, setGroupedChatsList] = useState([]);
  const [archivedList, setArchivedList] = useState([]);
  const [isHistoryLoading, setIsHistoryLoading] = useState(true);
  const [isArchivedLoading, setIsArchivedLoading] = useState(true);
  const [dialogInput, setDialogInput] = useState("");
  const [renameDialog, setRenameDialog] = useState({
    open: false,
    item: null,
    currentTab: null,
  });

  const hasFetchedArchivedChatsRef = useRef(false);
  const tokenStr = localStorage.getItem("access_token");

  const fetchChatHistory = useCallback(async () => {
    try {
      setIsHistoryLoading(true);
      const res = await fetch(
        `${config.baseUrl}chat/chatbot-messages-groups/chat-list/`,
        {
          headers: {
            Authorization: `Bearer ${tokenStr}`,
            "Content-Type": "application/json",
          },
        },
      );
      if (res.ok) {
        const data = await res.json();
        setGroupedChatsList(data.grouped_chats || []);
        setHistoryList(data.ungrouped_chats || []);
      }
    } catch (err) {
      console.error("Error loading active chats:", err);
    } finally {
      setIsHistoryLoading(false);
    }
  }, [tokenStr]);

  const fetchArchivedList = useCallback(async () => {
    try {
      setIsArchivedLoading(true);
      const res = await fetch(
        `${config.baseUrl}chat/chatbot-messages/?is_archived=true`,
        {
          headers: {
            Authorization: `Bearer ${tokenStr}`,
            "Content-Type": "application/json",
          },
        },
      );
      if (res.ok) {
        setArchivedList(await res.json());
        hasFetchedArchivedChatsRef.current = true;
      }
    } catch (err) {
      console.error("Error loading archived chats:", err);
    } finally {
      setIsArchivedLoading(false);
    }
  }, [tokenStr]);

  const handleCreateGroup = useCallback(
    async (groupName) => {
      try {
        const res = await fetch(
          `${config.baseUrl}chat/chatbot-messages-groups/`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${tokenStr}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify({ name: groupName }),
          },
        );

        if (res.ok) {
          await fetchChatHistory();
          return true;
        } else {
          const errData = await res.json().catch(() => ({}));
          const errMsg =
            errData?.name?.[0] || "Failed to create group. Please try again.";
          setSnackbar({ open: true, message: errMsg });
          setSnackbarMessage(errMsg);
          setSnackbarOpen(true);
          console.error("Failed to create group:", errData);
          return false;
        }
      } catch (err) {
        console.error("Error creating group:", err);
        setSnackbar({ open: true, message: "Something went wrong." });
        return false;
      }
    },
    [tokenStr, fetchChatHistory],
  );

  const handleSnackbarClose = (event, reason) => {
    if (reason === "clickaway") return;
    setSnackbarOpen(false);
  };

  const executeAssignGroup = useCallback(
    async (chat, groupId) => {
      const { session_id } = chat;
      try {
        const res = await fetch(
          `${config.baseUrl}chat/chatbot-messages/${session_id}/`,
          {
            method: "PATCH",
            headers: {
              Authorization: `Bearer ${tokenStr}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify({ chatbot_message_group: groupId }),
          },
        );
        if (res.ok) {
          await fetchChatHistory();
        } else {
          console.error("Failed to assign group:", await res.text());
        }
      } catch (err) {
        console.error("Error assigning group:", err);
      }
    },
    [tokenStr, fetchChatHistory],
  );

  const handleNewChat = useCallback(() => {
    sessionStorage.removeItem("agentic_chat_state");
    setChatKey((prev) => prev + 1);
    navigate("/calque-ai");
  }, [navigate]);

  const handleSelectSession = useCallback(
    (sessionId) => {
      sessionStorage.removeItem("agentic_chat_state");
      navigate(`/calque-ai/${sessionId}`);
    },
    [navigate],
  );

  const handleRenameConfirm = async (newTitle) => {
    const { item, currentTab } = renameDialog;
    setRenameDialog({ open: false, item: null, currentTab: null });

    try {
      await fetch(
        `${config.baseUrl}chat/chatbot-messages/${item.session_id}/`,
        {
          method: "PATCH",
          headers: {
            Authorization: `Bearer ${tokenStr}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ title: newTitle }),
        },
      );

      if (currentTab === "Archive") await fetchArchivedList();
      else await fetchChatHistory();
    } catch (err) {
      console.error("Rename failed:", err);
    }
  };

  const executeArchiveChat = async (item) => {
    const { session_id } = item;
    try {
      const res = await fetch(
        `${config.baseUrl}chat/chatbot-messages/${session_id}/`,
        {
          method: "PATCH",
          headers: {
            Authorization: `Bearer ${tokenStr}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ is_archived: true }),
        },
      );

      if (res.ok) {
        if (activeSessionId === session_id) handleNewChat();

        await fetchChatHistory();
        if (hasFetchedArchivedChatsRef.current) {
          await fetchArchivedList();
        }
      } else {
        console.error("Failed to archive chat:", await res.text());
      }
    } catch (err) {
      console.error("Archive failed:", err);
    }
  };

  const executeUnarchiveChat = async (item) => {
    const { session_id } = item;
    try {
      const res = await fetch(
        `${config.baseUrl}chat/chatbot-messages/${session_id}/`,
        {
          method: "PATCH",
          headers: {
            Authorization: `Bearer ${tokenStr}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ is_archived: false }),
        },
      );

      if (res.ok) {
        setArchivedList((prev) =>
          prev.filter((c) => c.session_id !== session_id),
        );
        await fetchChatHistory();
      } else {
        console.error("Failed to unarchive chat:", await res.text());
      }
    } catch (err) {
      console.error("Unarchive failed:", err);
    }
  };

  const executeRenameFolder = useCallback(
    async (group) => {
      try {
        const res = await fetch(
          `${config.baseUrl}chat/chatbot-messages-groups/${group.id}/`,
          {
            method: "PATCH",
            headers: {
              Authorization: `Bearer ${tokenStr}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify({ name: group.name }),
          },
        );
        if (res.ok) {
          await fetchChatHistory();
        } else {
          const errData = await res.json().catch(() => ({}));
          const errMsg =
            errData?.name?.[0] || "Failed to rename group. Please try again.";
          setSnackbarMessage(errMsg);
          setSnackbarOpen(true);
          console.error("Failed to rename group:", errData);
        }
      } catch (err) {
        console.error("Error renaming group:", err);
        setSnackbarMessage("Something went wrong.");
        setSnackbarOpen(true);
      }
    },
    [tokenStr, fetchChatHistory],
  );

  const executeDeleteFolder = useCallback(
    async (group) => {
      try {
        const res = await fetch(
          `${config.baseUrl}chat/chatbot-messages-groups/${group.id}/`,
          {
            method: "DELETE",
            headers: {
              Authorization: `Bearer ${tokenStr}`,
              "Content-Type": "application/json",
            },
          },
        );
        if (res.ok) {
          await fetchChatHistory();
        } else {
          console.error("Failed to delete group:", await res.text());
        }
      } catch (err) {
        console.error("Error deleting group:", err);
      }
    },
    [tokenStr, fetchChatHistory],
  );

  const handleExecuteAction = useCallback(
    async (actionType, item, currentTab) => {
      switch (actionType) {
        case "rename":
          setDialogInput(item.title || "");
          setRenameDialog({ open: true, item, currentTab });
          break;
        case "archive":
          await executeArchiveChat(item);
          break;
        case "unarchive":
          await executeUnarchiveChat(item);
          break;
        case "renameFolder":
          await executeRenameFolder(item);
          break;
        case "deleteFolder":
          await executeDeleteFolder(item);
          break;
        case "assignGroup":
          await executeAssignGroup(item.chat, item.groupId);
          break;
        case "removeFromGroup":
          await executeAssignGroup(item.chat, null);
          break;
        default:
          console.warn(`Unhandled action variant passed: ${actionType}`);
      }
    },
    [
      activeSessionId,
      handleNewChat,
      tokenStr,
      executeRenameFolder,
      executeDeleteFolder,
      executeAssignGroup,
    ],
  );

  const userName =
    JSON.parse(localStorage.getItem("user_info") || "{}")?.first_name || "";

  useEffect(() => {
    fetchChatHistory();
  }, [fetchChatHistory]);

  return (
    <Box
      sx={{
        display: "flex",
        height: "calc(100vh - 100px)",
        overflow: "hidden",
        marginTop: "5px",
      }}
    >
      <ChatSidebar
        sidebarOpen={sidebarOpen}
        setSidebarOpen={setSidebarOpen}
        handleNewChat={handleNewChat}
        historyList={historyList}
        groupedChatsList={groupedChatsList}
        archiveList={archivedList}
        isHistoryLoading={isHistoryLoading}
        isArchivedLoading={isArchivedLoading}
        activeSessionId={activeSessionId}
        onSelectSession={handleSelectSession}
        onExecuteAction={handleExecuteAction}
        onArchiveTabOpen={fetchArchivedList}
        onHistoryTabOpen={fetchChatHistory}
        archiveFetched={hasFetchedArchivedChatsRef.current}
        onCreateGroup={handleCreateGroup}
      />
      <ChatWindow
        key={chatKey}
        userName={userName}
        activeSessionId={activeSessionId}
        refreshHistoryList={fetchChatHistory}
        sidebarOpen={sidebarOpen}
      />

      <InputDialog
        open={renameDialog.open}
        title="Rename Chat"
        placeholder="Enter chat name"
        confirmText="Rename"
        value={dialogInput}
        onChange={setDialogInput}
        onConfirm={() => handleRenameConfirm(dialogInput)}
        onCancel={() =>
          setRenameDialog({ open: false, item: null, currentTab: null })
        }
      />

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          severity="error"
          variant="filled"
          onClose={handleSnackbarClose}
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Layout(Chatbot);
